import React from 'react';
import { Pressable, StyleSheet, Text, View, type ViewStyle } from 'react-native';
import { D } from './theme';

/**
 * 인더스트리얼 배색의 공용 부품. 홈 피드에서 정한 규칙을 나머지 화면이 그대로 쓴다.
 *
 * 규칙: 콘크리트 바탕 · 흰 판에 검은 테두리 1px · 모서리는 각지게 ·
 * 형광 노랑은 '지금 선택된 것' 과 '눌러야 할 것' 에만. 빨강은 할인율에만.
 */
export function Panel({ children, style }: { children: React.ReactNode; style?: ViewStyle }) {
  return <View style={[s.panel, style]}>{children}</View>;
}

/** 화면 머리. 작은 설명줄이 위, 제목이 아래 — 홈과 같은 순서를 지킨다. */
export function Head({ kicker, title, right }: { kicker?: string; title: string; right?: React.ReactNode }) {
  return (
    <View style={s.head}>
      <View style={{ flex: 1, gap: 1, minWidth: 0 }}>
        {kicker ? <Text style={s.kicker}>{kicker}</Text> : null}
        <Text style={s.h1}>{title}</Text>
      </View>
      {right}
    </View>
  );
}

/** 지금 선택된 것만 검은 판 + 형광 글자. 색 대비가 아니라 반전으로 읽히게 한다. */
export function Chip({ label, on, onPress }: { label: string; on?: boolean; onPress?: () => void }) {
  return (
    <Pressable onPress={onPress} style={[s.chip, on && s.chipOn]}>
      <Text style={[s.chipText, on && s.chipTextOn]}>{label}</Text>
    </Pressable>
  );
}

/** 눌러야 할 것. 검은 바탕에 형광 라벨 — 홈의 'AI 에게 맡기기' 와 같은 문법. */
export function Action({ label, sub, onPress, disabled }: { label: string; sub?: string; onPress: () => void; disabled?: boolean }) {
  return (
    <Pressable onPress={onPress} disabled={disabled} style={({ pressed }) => [s.action, disabled && { opacity: 0.4 }, pressed && { opacity: 0.85 }]}>
      <Text style={s.actionLabel}>{label}</Text>
      {sub ? <Text style={s.actionSub}>{sub}</Text> : null}
    </Pressable>
  );
}

/** 작은 대문자 라벨. 값이 아니라 항목 이름에만 쓴다. */
export function Label({ children }: { children: string }) {
  return <Text style={s.label}>{children}</Text>;
}

export function Tag({ label, tone = 'ink' }: { label: string; tone?: 'ink' | 'volt' | 'alert' | 'quiet' }) {
  const bg = tone === 'volt' ? D.volt : tone === 'alert' ? D.alert : tone === 'quiet' ? D.line : D.ink;
  const fg = tone === 'volt' ? D.ink : tone === 'quiet' ? D.ink2 : tone === 'alert' ? '#FFFFFF' : D.volt;
  return (
    <View style={[s.tag, { backgroundColor: bg }]}>
      <Text style={[s.tagText, { color: fg }]}>{label}</Text>
    </View>
  );
}

export function Empty({ title, body }: { title: string; body: string }) {
  return (
    <View style={s.empty}>
      <Text style={s.emptyTitle}>{title}</Text>
      <Text style={s.emptyBody}>{body}</Text>
    </View>
  );
}

const s = StyleSheet.create({
  panel: { backgroundColor: D.panel, borderWidth: 1, borderColor: D.ink, padding: 14 },

  head: { flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, paddingHorizontal: 16, paddingBottom: 12 },
  kicker: { fontSize: 10, fontWeight: '700', color: D.ink3, letterSpacing: 0.3 },
  h1: { fontSize: 22, fontWeight: '900', color: D.ink, letterSpacing: -0.5 },

  chip: { paddingVertical: 6, paddingHorizontal: 11, backgroundColor: D.panel, borderWidth: 1, borderColor: D.ink },
  chipOn: { backgroundColor: D.ink },
  chipText: { fontSize: 11.5, fontWeight: '800', color: D.ink },
  chipTextOn: { color: D.volt },

  action: { backgroundColor: D.ink, paddingVertical: 13, paddingHorizontal: 14, gap: 3, alignItems: 'center' },
  actionLabel: { fontSize: 15, fontWeight: '900', color: D.volt, letterSpacing: -0.2 },
  actionSub: { fontSize: 11, fontWeight: '600', color: '#FFFFFF', opacity: 0.75 },

  label: { fontSize: 9.5, fontWeight: '800', color: D.ink3, letterSpacing: 0.9 },
  tag: { paddingVertical: 3, paddingHorizontal: 7 },
  tagText: { fontSize: 10.5, fontWeight: '900', letterSpacing: 0.2 },

  empty: { paddingTop: 56, paddingHorizontal: 16, alignItems: 'center', gap: 6 },
  emptyTitle: { fontSize: 16, fontWeight: '800', color: D.ink },
  emptyBody: { fontSize: 13, color: D.ink2, textAlign: 'center', lineHeight: 20 },
});
